/** Output device selector — lets the user pick an audio output device via sinkId.
 *
 * Only renders when the browser supports `AudioContext.setSinkId`
 * and `navigator.mediaDevices.enumerateDevices`.
 */

import { Volume2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

/** Props for the OutputDeviceSelector component. */
export type OutputDeviceSelectorProps = {
	readonly audioContext: AudioContext | null;
};

/** A simplified audio output device descriptor. */
type OutputDevice = {
	readonly deviceId: string;
	readonly label: string;
};

/** Check whether the current browser supports AudioContext.setSinkId. */
function supportsSinkId(): boolean {
	return (
		typeof AudioContext !== "undefined" && "setSinkId" in AudioContext.prototype
	);
}

export function OutputDeviceSelector({
	audioContext,
}: OutputDeviceSelectorProps) {
	const [devices, setDevices] = useState<readonly OutputDevice[]>([]);
	const [currentId, setCurrentId] = useState<string>("");
	const [visible, setVisible] = useState(false);

	// Enumerate audio output devices on mount and when the audioContext arrives.
	useEffect(() => {
		if (!supportsSinkId() || !audioContext) return;
		setVisible(true);

		let cancelled = false;

		async function enumerate() {
			try {
				const all = await navigator.mediaDevices.enumerateDevices();
				if (cancelled) return;
				const outputs: OutputDevice[] = all
					.filter((d) => d.kind === "audiooutput")
					.map((d) => ({ deviceId: d.deviceId, label: d.label || d.deviceId }));
				setDevices(outputs);
			} catch {
				// Permission denied or not supported — hide the selector.
				setVisible(false);
			}
		}

		enumerate();

		// Re-enumerate when devices change (headphones plugged in, etc.).
		navigator.mediaDevices.addEventListener("devicechange", enumerate);
		return () => {
			cancelled = true;
			navigator.mediaDevices.removeEventListener("devicechange", enumerate);
		};
	}, [audioContext]);

	const handleChange = useCallback(
		async (e: React.ChangeEvent<HTMLSelectElement>) => {
			const id = e.target.value;
			if (!audioContext) return;
			try {
				// biome-ignore lint/suspicious/noExplicitAny: setSinkId not in all TS lib types
				await (audioContext as any).setSinkId(id);
				setCurrentId(id);
			} catch (err) {
				console.error("Failed to set audio output device:", err);
			}
		},
		[audioContext],
	);

	if (!visible || devices.length <= 1) return null;

	return (
		<div className="output-device-selector">
			<Volume2 size={12} />
			<select
				className="output-device-select"
				value={currentId}
				onChange={handleChange}
				title="Select audio output device"
			>
				{devices.map((d) => (
					<option key={d.deviceId} value={d.deviceId}>
						{d.label}
					</option>
				))}
			</select>
		</div>
	);
}
